import React, { useState, useEffect, useMemo } from 'react';
import { Check, Ruler } from 'lucide-react';

const ProductConfigurator = ({ product, onConfigChange, onPriceChange }) => {
  const [selectedFabric, setSelectedFabric] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [customDims, setCustomDims] = useState({ width: '', depth: '', length: '' });
  const [useCustom, setUseCustom] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(product.price);

  const freeEnabled = !!product.free_dimensions_enabled;
  const freeMode = product.free_dimensions_mode || 'm2';

  // Auto-ativa medidas livres se não houver tamanhos pré-definidos
  useEffect(() => {
    if (freeEnabled && (!product.size_options || product.size_options.length === 0)) {
      setUseCustom(true);
    }
  }, [freeEnabled, product.size_options]);

  const customArea = useMemo(() => {
    const w = parseFloat(customDims.width) || 0;
    const d = parseFloat(customDims.depth) || 0;
    return (w * d) / 10000; // cm × cm → m²
  }, [customDims.width, customDims.depth]);

  const customLength = useMemo(() => {
    const l = parseFloat(customDims.length) || 0;
    return l / 100; // cm → m
  }, [customDims.length]);

  useEffect(() => {
    let price = product.price || 0;

    if (useCustom && freeEnabled) {
      if (freeMode === 'm2' && selectedFabric && customArea > 0) {
        price += (selectedFabric.price_per_m2 || 0) * customArea;
      }
      if (freeMode === 'linear' && customLength > 0) {
        price += (product.price_per_linear_meter || 0) * customLength;
      }
    } else if (selectedFabric && selectedSize) {
      price += (selectedFabric.price_per_m2 || 0) * (selectedSize.m2_needed || 0);
    }

    if (selectedMaterial) price += selectedMaterial.price_extra || 0;

    setCalculatedPrice(price);
    onPriceChange(price);
    onConfigChange({
      fabric: selectedFabric,
      size: useCustom ? null : selectedSize,
      material: selectedMaterial,
      custom_dimensions: useCustom
        ? freeMode === 'm2'
          ? { mode: 'm2', width_cm: parseFloat(customDims.width) || 0, depth_cm: parseFloat(customDims.depth) || 0, area_m2: customArea }
          : { mode: 'linear', length_cm: parseFloat(customDims.length) || 0, length_m: customLength }
        : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFabric, selectedSize, selectedMaterial, useCustom, customDims, freeMode]);

  if (!product.is_customizable) return null;

  const hasSizes = product.size_options && product.size_options.length > 0;

  return (
    <div className="space-y-8 border-t border-border pt-8 mt-8">
      <div>
        <h3 className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4">
          Personalize seu Produto
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Configure o produto exatamente como você deseja
        </p>
      </div>

      {product.fabric_options && product.fabric_options.length > 0 && (
        <div>
          <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4 block">
            Escolha o Tecido *
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {product.fabric_options.map((fabric) => {
              const pricePerM2 = typeof fabric.price_per_m2 === 'string' ? parseFloat(fabric.price_per_m2) : (fabric.price_per_m2 || 0);
              return (
                <button
                  key={fabric.id}
                  type="button"
                  data-testid={`fabric-option-${fabric.id}`}
                  onClick={() => setSelectedFabric({ ...fabric, price_per_m2: pricePerM2 })}
                  className={`relative p-4 border-2 transition-all duration-300 ${
                    selectedFabric?.id === fabric.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                >
                  {fabric.image && (
                    <div className="aspect-square mb-3 overflow-hidden bg-muted">
                      <img src={fabric.image} alt={fabric.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="text-left">
                    <p className="text-sm font-medium mb-1">{fabric.name}</p>
                    <p className="text-xs text-muted-foreground">R$ {pricePerM2.toFixed(2)}/m²</p>
                  </div>
                  {selectedFabric?.id === fabric.id && (
                    <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Toggle entre tamanhos pré-definidos e medidas livres */}
      {freeEnabled && hasSizes && (
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="use-preset-size"
            onClick={() => setUseCustom(false)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-all ${
              !useCustom ? 'bg-primary text-white border-primary' : 'border-border hover:border-primary'
            }`}
          >
            Tamanhos pré-definidos
          </button>
          <button
            type="button"
            data-testid="use-custom-size"
            onClick={() => setUseCustom(true)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-all flex items-center gap-2 ${
              useCustom ? 'bg-primary text-white border-primary' : 'border-border hover:border-primary'
            }`}
          >
            <Ruler className="w-3 h-3" strokeWidth={1.5} />
            Minhas medidas
          </button>
        </div>
      )}

      {/* Size Selection (pré-definidos) */}
      {hasSizes && !useCustom && (
        <div>
          <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4 block">
            Escolha o Tamanho *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {product.size_options.map((size) => {
              const m2Value = typeof size.m2_needed === 'string' ? parseFloat(size.m2_needed) : (size.m2_needed || 0);
              return (
                <button
                  key={size.id}
                  type="button"
                  data-testid={`size-option-${size.id}`}
                  onClick={() => setSelectedSize({ ...size, m2_needed: m2Value })}
                  className={`relative p-4 border-2 text-left transition-all duration-300 ${
                    selectedSize?.id === size.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium mb-1">{size.name}</p>
                      {size.description && (
                        <p className="text-xs text-muted-foreground">{size.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        {m2Value.toFixed(2)} m² de tecido
                      </p>
                    </div>
                    {selectedSize?.id === size.id && (
                      <div className="bg-primary text-white rounded-full p-1 flex-shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom Dimensions */}
      {freeEnabled && useCustom && (
        <div data-testid="custom-dimensions-panel" className="border-2 border-dashed border-primary/40 p-6 bg-primary/5">
          <div className="flex items-center gap-2 mb-4">
            <Ruler className="w-4 h-4 text-primary" strokeWidth={1.5} />
            <label className="font-accent text-xs uppercase tracking-widest text-primary">
              Digite suas medidas
            </label>
          </div>

          {freeMode === 'm2' ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Largura (cm)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  data-testid="dim-width"
                  value={customDims.width}
                  onChange={(e) => setCustomDims({ ...customDims, width: e.target.value })}
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm outline-none"
                  placeholder="Ex: 180"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Profundidade (cm)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  data-testid="dim-depth"
                  value={customDims.depth}
                  onChange={(e) => setCustomDims({ ...customDims, depth: e.target.value })}
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm outline-none"
                  placeholder="Ex: 90"
                />
              </div>
              <div className="col-span-2 text-xs text-muted-foreground mt-2" data-testid="dim-area">
                Área calculada: <strong>{customArea.toFixed(2)} m²</strong>
                {selectedFabric && customArea > 0 && (
                  <> · Tecido: R$ {((selectedFabric.price_per_m2 || 0) * customArea).toFixed(2)}</>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Comprimento (cm)</label>
              <input
                type="number"
                step="1"
                min="0"
                data-testid="dim-length"
                value={customDims.length}
                onChange={(e) => setCustomDims({ ...customDims, length: e.target.value })}
                className="w-full max-w-xs bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm outline-none"
                placeholder="Ex: 220"
              />
              <p className="text-xs text-muted-foreground mt-3" data-testid="dim-length-preview">
                Equivale a <strong>{customLength.toFixed(2)} m</strong>
                {product.price_per_linear_meter > 0 && customLength > 0 && (
                  <> · Cálculo: R$ {(customLength * (product.price_per_linear_meter || 0)).toFixed(2)}</>
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Material Selection */}
      {product.material_options && product.material_options.length > 0 && (
        <div>
          <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4 block">
            Material Extra (Opcional)
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {product.material_options.map((material) => {
              const priceExtra = typeof material.price_extra === 'string' ? parseFloat(material.price_extra) : (material.price_extra || 0);
              return (
                <button
                  key={material.id}
                  type="button"
                  data-testid={`material-option-${material.id}`}
                  onClick={() =>
                    setSelectedMaterial(selectedMaterial?.id === material.id ? null : { ...material, price_extra: priceExtra })
                  }
                  className={`relative p-4 border-2 text-left transition-all duration-300 ${
                    selectedMaterial?.id === material.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium mb-1">{material.name}</p>
                      {material.description && (
                        <p className="text-xs text-muted-foreground mb-2">{material.description}</p>
                      )}
                      <p className="text-xs font-medium text-primary">+ R$ {priceExtra.toFixed(2)}</p>
                    </div>
                    {selectedMaterial?.id === material.id && (
                      <div className="bg-primary text-white rounded-full p-1 flex-shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Price Breakdown */}
      {(selectedFabric || selectedMaterial || (useCustom && (customArea > 0 || customLength > 0))) && (
        <div className="border border-border p-6 bg-secondary/30">
          <h4 className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4">
            Detalhamento do Preço
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Preço Base</span>
              <span>R$ {product.price?.toFixed(2)}</span>
            </div>

            {useCustom && freeMode === 'm2' && selectedFabric && customArea > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {selectedFabric.name} ({customArea.toFixed(2)}m² × R$ {selectedFabric.price_per_m2?.toFixed(2)})
                </span>
                <span>R$ {((selectedFabric.price_per_m2 || 0) * customArea).toFixed(2)}</span>
              </div>
            )}

            {useCustom && freeMode === 'linear' && customLength > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Metro linear ({customLength.toFixed(2)}m × R$ {(product.price_per_linear_meter || 0).toFixed(2)})
                </span>
                <span>R$ {(customLength * (product.price_per_linear_meter || 0)).toFixed(2)}</span>
              </div>
            )}

            {!useCustom && selectedFabric && selectedSize && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {selectedFabric.name} ({selectedSize.m2_needed?.toFixed(2)}m² × R${' '}
                  {selectedFabric.price_per_m2?.toFixed(2)})
                </span>
                <span>
                  R$ {((selectedFabric.price_per_m2 || 0) * (selectedSize.m2_needed || 0)).toFixed(2)}
                </span>
              </div>
            )}

            {selectedMaterial && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{selectedMaterial.name}</span>
                <span>R$ {selectedMaterial.price_extra?.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between pt-3 border-t border-border font-medium text-base">
              <span>Preço Total</span>
              <span className="text-primary" data-testid="configurator-total">R$ {calculatedPrice.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Warnings */}
      {product.fabric_options?.length > 0 && !selectedFabric && (
        <p className="text-xs text-destructive">* Selecione um tecido para continuar</p>
      )}
      {!useCustom && hasSizes && !selectedSize && (
        <p className="text-xs text-destructive">* Selecione um tamanho para continuar</p>
      )}
      {useCustom && freeMode === 'm2' && (customArea === 0) && (
        <p className="text-xs text-destructive">* Informe largura e profundidade em cm</p>
      )}
      {useCustom && freeMode === 'linear' && (customLength === 0) && (
        <p className="text-xs text-destructive">* Informe o comprimento em cm</p>
      )}
    </div>
  );
};

export default ProductConfigurator;
